<script setup>
import { computed, nextTick, onMounted, provide, reactive, ref, toValue, watch } from 'vue'
import { omit, result } from 'lodash-es'
import { useStepper } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { complaintDefaultStep } from '@/router/index'
import { useUserStore } from '@provise/stores/user'
import { useUiStore } from '@provise/stores/ui'
import { useMessageStack } from '@provise/stores/messagestack'
import { useModalStack } from '@provise/stores/modalstack'
import { useApi, useApiResource } from '@provise/composables/api'
import { commons, transformer } from '@provise/lib/transformer'
import History from '@/components/history/history.vue'
import stepsConfig, { CLOSE_WITHOUT_REPLY, NO_APPROVE_SKIP } from './item/steps'
import Communication from './item/communication/communication.vue'
import Transfer from './item/transfer/form.vue'
import ProgressBar from './item/progressbar.vue'
import FinalForm from './item/final_form.vue'
import ComplaintDocs from './item/downloads.vue'
import { useToggle } from '@vueuse/core'
import { useTransfer } from '@/composables/complaints'
import { indicator as SLAIndic, link as SLALink, user as SLAUser } from './item/sla/index'

const [showDetailsToggle] = useToggle()
const { firstTransfer, transfer, fetchTransfers } = useTransfer()

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const props = defineProps({
  id: Number,
  step: {
    type: String,
    default: 'registration',
  },
})
const { blockScreen } = storeToRefs(useUiStore())
const { push: openModal } = useModalStack()
const { push: notify } = useMessageStack()
const stepsKeys = Object.keys(stepsConfig)
const stepsVisible = reactive(
  stepsKeys.reduce((m, k) => {
    m[k] = true
    return m
  }, {})
)
const cwrIdx = stepsKeys.indexOf(CLOSE_WITHOUT_REPLY)

const tr = transformer(
  [
    ['medicine', 'id'],
    ['channel', 'id'],
    ['name_type', 'id'],
    ['department', 'id'],
    ['assign_role', 'id'],
    ['sla_complaint', 'id'],
    ['author', 'id'],
    ['request_category', 'id'],
    ['uploads', 'ids'],
    ['subcomplaints', 'ids'],
  ],
  true,
  true
)
const { index, isCurrent, goTo, isFirst } = useStepper(
  stepsConfig,
  props.id ? null : complaintDefaultStep
)
const havePermission = computed(() => {
  const modelVal = toValue(model)
  const stepModel = modelVal.status?.key
  const stepProps = props.step
  const stepPropsIdx = stepsKeys.indexOf(stepProps)
  const cosIdx = stepsKeys.indexOf(modelVal.request_category?.close_on_stage)

  // закрытые и завершенные обращения редактировать нельзя
  if (['done', 'closed'].includes(stepModel)) {
    if (stepPropsIdx > cwrIdx) {
      if (cosIdx == -1 || cosIdx > cwrIdx) {
        return true
      }
    }
    return false
  }

  // админу можно все!
  if (userStore.can('sa')) return true

  // редактировать закрытые нельзя никому, кроме админа (см выше)
  if (['done', 'closed'].includes(stepProps)) {
    return false
  }

  // создавать обращения может инициатор
  if (stepProps === 'registration' && userStore.can('initiator')) {
    return true
  }

  // специфические права
  if (stepProps === 'prepare_to_answer') {
    return userStore.can('coordinator')
  }

  if (stepProps === 'coordination') {
    const isDone = replies.value?.find(r => r.get_state === REPLY_STATES.STATE_DONE) != null
    return (
      (userStore.can('coordinator') && isDone) ||
      userStore.can('signater') ||
      (userStore.can('supersignater') && model.value.need_commercial_approve)
    )
  }

  if (stepProps === 'investigation') {
    return userStore.can('investigator')
  }

  // все остальное можно только координатору
  return userStore.can('coordinator')
})

const approveLabel = computed(() => {
  const modelVal = toValue(model)
  const step = modelVal.status?.key || 'registration'
  const repliesVal = toValue(replies)
  const label = result(stepsConfig, [step, 'approveLabel'], 'Approve')
  const labelClose = 'Перейти к закрытию'
  const cos = modelVal.request_category?.close_on_stage

  // если у этапа нет ответственного, то переводить дальше его нельзя
  if (!modelVal.sla_users?.[step]) {
    return
  }

  // если не указан date_eqv_finish, то дальше не пускаем
  if (step === 'investigation' && modelVal.date_eqv_finish == null) {
    return
  }

  // некоторые этапы должны закрываться раньше времени
  if (cos === step) {
    return labelClose
  }
  if (step === 'investigation' && modelVal.no_approve) {
    return labelClose
  }

  // регать может только coordinator
  if (step === 'registration' && !userStore.can(['coordinator', 'initiator'])) {
    return null
  }

  // если нет ни одного ответа, то кнопку перехода дальше не показывать
  if (step === 'prepare_to_answer') {
    if (repliesVal.length === 0) {
      return
    }
    if (
      !repliesVal.some(r =>
        [REPLY_STATES.STATE_PREPARE, REPLY_STATES.STATE_WAIT, REPLY_STATES.STATE_DONE].includes(
          r.get_state
        )
      )
    ) {
      return
    }
  }

  // если нет ни одного согласованного ответа, то кнопку перехода дальше не показывать
  if (step === 'coordination') {
    if (!repliesVal.find(r => r.get_state === REPLY_STATES.STATE_DONE)) {
      return
    }
  }

  if (step === 'delivery_answer') {
    const cosIdx = stepsKeys.indexOf(cos)

    if (cosIdx != -1 && cosIdx <= cwrIdx) {
      return label
    }

    if (!repliesVal.some(r => r.communication?.sent)) {
      return
    }
  }

  if (step == 'correction' && !modelVal.request_category?.id) {
    return
  }

  return label
})
const backLabel = computed(() => {
  let label = result(stepsConfig, [model.value?.status?.key, 'backLabel'])
  if (label) return label
  // if (userStore.can('sa')) return 'Назад (админ)'
  return null
})
const headerLabel = computed(() => {
  return model.value.id ? 'Обращение ID=' + model.value?.id : 'Новое обращение'
})
const model = ref({
  id: props.id || null,
  status: props.id ? null : { key: complaintDefaultStep, id: 1 },
})
const flowstep = ref(null)
const { refresh: fetchReplies, model: replies } = useApi({
  url: computed(() => `complaints/${model.value.id}/reply`),
  data: [],
  // immediate: true,
  before: () => (model.value.id ? true : false),
  pick: d => (d.data || []).map(replyMapping(model)),
})
const editables = computed(() => {
  const keys = Object.keys(stepsConfig)
  const fsIdx = keys.indexOf(flowstep.value)
  const eds = {}
  const noApprove = model.value.no_approve

  for (const k of stepsKeys) {
    if (noApprove && NO_APPROVE_SKIP.includes(k)) {
      eds[k] = false
      continue
    }
    eds[k] = stepsKeys.indexOf(k) <= fsIdx
  }
  return eds
})
const { refresh } = useApi({
  model,
  transform,
  pick: d => {
    d.date_created = commons.dateLocal(d.date_created, 'Pp')
    d.sla_users = {}
    for (let item of d.sla_complaint) {
      d.sla_users[item.status] = item.assign_user
    }
    return d
  },
})
const apiResource = useApiResource('complaints', model, refresh)
const zip = reactive(
  stepsKeys.reduce((m, k) => {
    m[k] = true
    return m
  }, {})
)

const { model: users } = useApi({
  url: 'users',
  query: { service: 'qems', onPage: 99999, page: 1 },
  data: [],
  pick: d =>
    (d.data || [])
      .filter(u =>
        userStore.can(
          ['initiator', 'coordinator', 'signater', 'supersignater', 'investigator', 'coordinator'],
          u
        )
      )
      .map(u => {
        return {
          ...u,
          label: u.fio || u.username,
        }
      }),
  immediate: true,
})

const { model: categories, refresh: categoriesFetch } = useApi({
  url: 'request_category',
  query: { onPage: 100 },
  immediate: true,
  data: [],
  pick: buildRequestCategories,
})

watch(
  () => model.value?.status?.key,
  statusKey => {
    if (statusKey == 'closed') {
      return
    }
    if (statusKey && statusKey != flowstep.value) {
      flowstep.value = statusKey
      _goto(statusKey)
    }
    if (['prepare_to_answer', 'coordination', 'delivery_answer'].includes(statusKey)) {
      fetchReplies()
    }
  }
)

watch(index, animateZip)

watch(
  () => route.params,
  r => {
    if (r.step) {
      goTo(r.step)
    }
  },
  { immediate: true }
)
watch(
  [() => model.value.request_category, () => model.value.no_approve, categories],
  ([rc, noapprove, cats]) => {
    let cosIndex = -1
    if (rc?.id && cats?.length) {
      const cat = cats.find(c => rc.id == c.id)
      if (cat?.close_on_stage) {
        cosIndex = stepsKeys.indexOf(cat.close_on_stage)
      }
    }
    for (let i = 0; i < stepsKeys.length; i++) {
      const stepk = stepsKeys[i]
      if (noapprove && NO_APPROVE_SKIP.includes(stepk)) {
        stepsVisible[stepk] = false
        continue
      }
      if (i == stepsKeys.length - 1) {
        stepsVisible[stepk] = true
      } else {
        stepsVisible[stepk] = cosIndex == -1 || i <= cosIndex
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if (props.id) {
    await apiResource('get')
    await Promise.all([fetchReplies(), categoriesFetch()])
    animateZip(stepsKeys.indexOf(model.value.status?.key))
  } else {
    flowstep.value = complaintDefaultStep
    goTo(complaintDefaultStep)
    zip[complaintDefaultStep] = false
  }
  if (route.hash == '#transfer') {
    fetchTransfers()
  }
})

function buildRequestCategories(d) {
  return (d.data || [])
    .filter(cat => cat.display)
    .map(m => {
      return {
        ...m,
        label: m.parent ? `${m.parent.name} / ${m.name}` : m.name,
      }
    })
}

function animateZip(c) {
  for (const sn of stepsKeys) {
    zip[sn] = true
  }
  const k = stepsKeys.at(c)
  scrollonactive(k)
  zip[k] = !isCurrent(k)
}

function scrollonactive(i) {
  const stp = document.getElementById(`complaints-step-${i}`)
  if (!stp) {
    return
  }
  stp.scrollIntoView({ behavior: 'smooth' })
}

function transform(d) {
  const v = tr({
    ...d,
    date_reported: props.id ? d.date_reported : new Date(),
  })
  return omit(v, [
    'status',
    'transfers',
    'date_created',
    'date_closed',
    'deleted',
    'id',
    'author',
    'assign_role',
    'sla_complaint',
    'assign_user',
    'sla_users',
  ])
}

function mergeReply(...newList) {
  const l = toValue(replies)
  const rmmap = replyMapping(model)
  for (const nrep of newList) {
    const i = l.findIndex(rep => rep.id == nrep.id)
    if (i === -1) {
      l.push(rmmap(nrep))
    } else {
      l[i] = rmmap(nrep)
    }
  }
  replies.value = l
}

function removeReply(nrep) {
  const l = toValue(replies)
  const i = l.findIndex(rep => rep.id == nrep.id)
  if (i === -1) {
    return
  }
  l.splice(i, 1)
  replies.value = l
}

async function onSave(trigger, autosave) {
  // костыль. надо передавать параметр, чтобы понимать закачивается файл или идет сохранение
  // для отправки сообщений по департаментам
  model.value = { ...model.value, autosave }
  try {
    await apiResource()
    if (trigger instanceof Function) {
      trigger(!props.id)
    }
  } finally {
    blockScreen.value = false
  }
  notify('Обращение сохранено')
  if (!props.id) {
    await router.push({ name: 'complaint.item', params: { id: model.value.id } })
    scrollonactive(1)
  }
}

async function closeAsSpam() {
  model.value = { ...model.value, spam_close: true }
  await onSave()
  notify('Обращение закрыто как спам')
  router.push({ name: 'complaint.list' })
}

let isProcessing = false

async function onNext(d) {
  if (isProcessing) return
  isProcessing = true

  const display = model.value.request_category?.display || ''
  if (
    model.value.status.key === 'delivery_answer' &&
    ['archive'].some(field => display.includes(field))
  ) {
    openModal({
      component: FinalForm,
      props: {
        headerText: 'Завершение обращения',
        model: model.value,
      },
      onClose: async result => {
        if (result?.completed) {
          await onSave()
          handleNextStep(d)
        }
        isProcessing = false
      },
    })
  } else {
    await handleNextStep(d)
    isProcessing = false
  }
}

async function handleNextStep(d) {
  await useApi({ url: `complaints/${model.value.id}/${d ? 'approve' : 'decline'}` }).refresh()
  await apiResource('get')

  let step = model.value.status.key
  notify(`Обращение теперь на этапе «${model.value.status.value}»`)
  if (step == 'done') {
    step = stepsKeys[stepsKeys.length - 1]
  }
  await _goto(step)
}

async function _goto(step) {
  const params = { name: 'complaint.item.step', params: { id: model.value.id, step } }
  if (route.hash == '#communication') {
    params.name = 'complaint.item.communication'
  }
  if (route.hash == '#transfer' && firstTransfer.value) {
    params.name = 'complaint.item.transfer'
    await getTransfer()
  }
  await router.replace(params)
}

provide('replies', [replies, fetchReplies, mergeReply, removeReply])
provide('complaint', model)
provide('users', users)
provide('apiResource', apiResource)
provide('categories', categories)

async function getTransfer() {
  if (firstTransfer.value) {
    transfer.value = { id: firstTransfer.value.id }
    showDetailsToggle()
  }
}
const s = computed(() => stepsConfig[props.step])
</script>

<script>
import DOMPurify from 'dompurify'
export const REPLY_STATES = {
  STATE_DONE: 'done',
  STATE_DENY: 'deny',
  STATE_PREPARE: 'prepare',
  STATE_WAIT: 'wait',
  STATE_WAIT_COMMERCE: 'wait_commerce',
}

function get_reply_state(model, reply) {
  if (reply.aligned === false || reply.commerce_aligned === false) return REPLY_STATES.STATE_DENY

  if (model.value.need_commercial_approve) {
    if (reply.assign_role == null) return REPLY_STATES.STATE_PREPARE
    if (reply.aligned === true && reply.commerce_aligned === true) return REPLY_STATES.STATE_DONE
    else if (reply.aligned === true) return REPLY_STATES.STATE_WAIT_COMMERCE
    return REPLY_STATES.STATE_WAIT
  }
  if (reply.aligned === true) return REPLY_STATES.STATE_DONE
  if (reply.assign_role !== null) return REPLY_STATES.STATE_WAIT
  return REPLY_STATES.STATE_PREPARE
}

export function replyMapping(model) {
  return r => {
    const res = {
      ...r,
      complaint: {
        id: model.value.id,
        need_commercial_approve: model.value.need_commercial_approve,
        status: model.value.status,
      },
      display_name: r.name,
      approval_comment_label: DOMPurify.sanitize(r.approval_comment.replace(/\n/g, '<br />')),
      approval_comment: '',
    }
    if (r.name && r.name.length) {
      res.display_name = r.name
    }
    if (r.uploads.length) {
      if (!res.display_name) {
        res.display_name = r.uploads[0].filename
      }
      res.link = '/api/v1.0/download/' + r.uploads[0].secure_filename
    }
    res.get_state = get_reply_state(model, r)
    return res
  }
}
</script>
<template>
  <div class="mb-2 d-flex align-items-center">
    <button
      class="btn btn-outline-primary"
      @click.prevent="$router.push({ name: 'complaint.list' })"
    >
      <x-icon icon="chevron-left" />
    </button>
    <ComplaintDocs v-if="id" :id="id" class="ms-auto" />
    <History v-if="id" model="Complaints" :id="id" ref="history" :is-small="true" class="ms-0" />
    <RouterLink :to="{ name: 'complaint.item.communication' }" class="btn btn-secondary" v-if="id">
      <i class="bi bi-chat" />
    </RouterLink>
  </div>

  <div class="vstack gap-3 mb-4 complaint-header">
    <div class="card">
      <div class="card-header d-flex">
        <div class="me-auto fw-bold">{{ headerLabel }}</div>
        <div v-if="model.date_created">Создано {{ model.date_created }}</div>
      </div>
    </div>
  </div>

  <ProgressBar
    :editables="editables"
    :flowstep="flowstep"
    :closed="['done', 'closed'].includes(model?.status?.key)"
    :leap-steps="stepsVisible"
  />

  <!-- Cards -->
  <div class="card">
    <div class="card-header d-flex align-items-center">
      <div class="me-auto d-flex align-items-center gap-2">
        <span :class="{ 'text-muted': !stepsVisible[step] }">{{ s.title }}</span>
        <div style="min-width: 16rem" v-if="stepsVisible[step]">
          <SLAUser :complaint="model" :step="step" @extend="apiResource('get')" v-if="model.id" />
        </div>
      </div>
      <SLAIndic :complaint="model" :step="step" v-if="stepsVisible[step]" />
    </div>
    <div class="card-body" v-if="!zip[step]">
      <component
        :is="s.component"
        edit
        :edit-permission="havePermission"
        v-model="model"
        @save="onSave"
        @refresh="apiResource('get')"
      >
        <template #sla>
          <SLALink :complaint="model" :step="step" @extended="apiResource('get')" />
        </template>
        <template #form-post-controls>
          <div class="hstack gap-2 form-post-controls" v-if="step == flowstep && havePermission">
            <button
              type="button"
              class="btn btn-outline-warning"
              @click="() => onNext(1)"
              v-if="approveLabel && model.id"
            >
              <x-icon icon="check" />
              {{ approveLabel }}
            </button>
            <button
              type="button"
              class="btn btn-outline-danger"
              @click="() => onNext(0)"
              v-if="backLabel && !isFirst"
            >
              <x-icon icon="xmark" />
              {{ backLabel }}
            </button>
            <button
              type="button"
              class="btn btn-outline-danger"
              @click="() => closeAsSpam()"
              v-if="model.id && !model.spam_close && isFirst && model.channel?.key == 'email'"
            >
              <x-icon icon="xmark" />
              Закрыть как спам
            </button>
          </div>
          <button
            type="button"
            class="btn btn-outline-danger"
            disabled="disabled"
            v-if="model.id && model.spam_close"
          >
            <x-icon icon="xmark" />
            Закрыто как спам
          </button>
        </template>
      </component>
    </div>
  </div>

  <Teleport to="body">
    <Communication
      :complaint-id="model.id"
      v-if="$route.name == 'complaint.item.communication'"
      @close="$router.replace({ name: 'complaint.item.step', params: { id, step } })"
    />
  </Teleport>
  <Teleport to="body">
    <Transfer
      v-if="$route.name == 'complaint.item.transfer'"
      @close="$router.replace({ name: 'complaint.item.step', params: { id, step } })"
    />
  </Teleport>
</template>
